import { CommonModule } from '@angular/common';
import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CartService } from 'app/core/cart/cart.service';
import { ApiService } from 'app/shared/api.service';

declare var Splide: any;

interface VideoboothPackage {
    id: string;
    name: string;
    price: number;
    regularPrice: number;
    features: string[];
}

interface ApiNonThemedPackage {
    id: string;
    non_themed_product_id: string;
    non_themed_product_name: string;
    name: string;
    description: string | null;
    regular_price: number;
    price: number;
    is_active: boolean;
    product_package_type: string;
    devices: any[];
}

@Component({
  selector: 'app-videobooth',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './videobooth.component.html',
  styleUrl: './videobooth.component.scss'
})
export class VideoboothComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('splideElement') splideElement!: ElementRef;
    splide: any = null;

    isLoading: boolean = false;
    packages: VideoboothPackage[] = [];

    videos: Array<{
        id: number;
        src: string;
        title: string;
        isMuted: boolean;
    }> = [
        { id: 1, src: '/assets/visidea/video360-1.mp4', title: 'Rolly Videobooth 360 #1', isMuted: true },
        { id: 2, src: '/assets/visidea/video360-2.mp4', title: 'Rolly Videobooth 360 #2', isMuted: true },
        { id: 3, src: '/assets/visidea/video360-3.mp4', title: 'Rolly Videobooth 360 #3', isMuted: true },
        { id: 4, src: '/assets/visidea/video360-4.mp4', title: 'Rolly Videobooth 360 #4', isMuted: true },
        { id: 5, src: '/assets/visidea/video360-5.mp4', title: 'Rolly Videobooth 360 #5', isMuted: true },
        { id: 6, src: '/assets/visidea/video360-6.mp4', title: 'Rolly Videobooth 360 #6', isMuted: true },
        { id: 7, src: '/assets/visidea/video360.mp4', title: 'Rolly Videobooth 360 #7', isMuted: true },
    ];

    constructor(
        private cartService: CartService,
        private apiService: ApiService,
        private cdr: ChangeDetectorRef
    ) {}

    phone = '6282260084847';

    ngOnInit(): void {
        this.fetchVideoboothPackages();
    }

    async fetchVideoboothPackages(): Promise<void> {
        this.isLoading = true;

        try {
            const apiEndpoint = '/api/v1/front/public/non-themed-product-packages/list?paginate=false';
            let response: any = await this.apiService.get(apiEndpoint, false);

            if (!response || !response.success || !response.data?.list) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`);
                    if (devRes.ok) {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Fallback fetch error:', e);
                }
            }

            if (response && response.data && Array.isArray(response.data.list) && response.data.list.length > 0) {
                const videoboothPackages = response.data.list.filter((pkg: ApiNonThemedPackage) => 
                    pkg.non_themed_product_name.toLowerCase().includes('videobooth') ||
                    pkg.non_themed_product_name.toLowerCase().includes('rolly')
                );

                if (videoboothPackages.length > 0) {
                    this.packages = this.transformVideoboothPackages(videoboothPackages);
                }
            }
        } catch (error) {
            console.error('Error fetching videobooth packages:', error);
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    private transformVideoboothPackages(apiList: ApiNonThemedPackage[]): VideoboothPackage[] {
        return apiList.map((item) => {
            return {
                id: item.id,
                name: item.name,
                price: item.price,
                regularPrice: item.regular_price,
                features: this.getBenefits(item)
            };
        });
    }

    private getBenefits(pkg: any): string[] {
        // Try multiple possible benefit sources
        const benefitData = 
            pkg?.properties?.benefit || 
            pkg?.benefit || 
            pkg?.benefits || 
            pkg?.features ||
            pkg?.description;

        if (!benefitData) {
            return [];
        }

        // If it's already an array, return it
        if (Array.isArray(benefitData)) {
            return benefitData.filter(b => b && typeof b === 'string');
        }

        // If it's a string, try parsing as JSON first
        if (typeof benefitData === 'string') {
            try {
                const parsed = JSON.parse(benefitData);
                if (Array.isArray(parsed)) {
                    return parsed.filter(b => b && typeof b === 'string');
                }
            } catch (e) {
                // Not JSON, try splitting by newlines
                const lines = benefitData
                    .split(/\r?\n/)
                    .map(line => line.trim())
                    .filter(line => line.length > 0);
                
                if (lines.length > 0) {
                    return lines;
                }
            }
        }

        return [];
    }

    formatCurrency(val: number): string {
        if (!val && val !== 0) return 'Rp0';
        return 'Rp' + val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.initializeSplide();
        }, 150);
    }

    ngOnDestroy(): void {
        if (this.splide) {
            this.splide.destroy();
        }
    }

    initializeSplide(): void {
        if (this.splideElement) {
            if (this.splide) {
                this.splide.destroy();
            }

            this.splide = new Splide(this.splideElement.nativeElement, {
                type: 'loop',
                drag: 'free',
                focus: 'center',
                perPage: 3,
                gap: '24px',
                pagination: false,
                arrows: false,
                autoScroll: {
                    speed: 1,
                    pauseOnHover: true,
                    pauseOnFocus: true,
                },
                breakpoints: {
                    1280: {
                        perPage: 3,
                        gap: '20px',
                    },
                    1024: {
                        perPage: 2,
                        gap: '16px',
                    },
                    640: {
                        perPage: 1.2,
                        gap: '12px',
                    },
                },
            });

            if (typeof window !== 'undefined' && (window as any).splide?.Extensions?.AutoScroll) {
                this.splide.mount((window as any).splide.Extensions);
            } else {
                this.splide.mount();
            }
        }
    }

    /**
     * Toggle sound/mute on a specific video.
     * When unmuted, mutes all other video samples to prevent overlapping sound.
     */
    toggleSound(videoIndex: number, videoEl: HTMLVideoElement, event: Event): void {
        event.stopPropagation();
        const currentMuted = this.videos[videoIndex].isMuted;

        if (currentMuted) {
            // Mute all other videos first
            this.videos.forEach((v, idx) => {
                if (idx !== videoIndex) {
                    v.isMuted = true;
                    const otherEl = document.getElementById(`videobooth-sample-${idx}`) as HTMLVideoElement;
                    if (otherEl) {
                        otherEl.muted = true;
                    }
                }
            });

            // Unmute selected video
            this.videos[videoIndex].isMuted = false;
            videoEl.muted = false;
            videoEl.play().catch(() => {});
        } else {
            // Mute selected video
            this.videos[videoIndex].isMuted = true;
            videoEl.muted = true;
        }
    }

    whatsapp(packageName: string = '') {
        const message = `Halo, saya tertarik untuk memesan Rolly Videobooth 360 ${packageName ? 'paket ' + packageName : ''}. Bisa bantu saya dengan informasi lebih lanjut mengenai paket ini? Terima kasih!`;
        const encodedMessage = encodeURIComponent(message);
        const url = `https://wa.me/${this.phone}?text=${encodedMessage}`;
        window.open(url, '_blank');
    }

    addToCart(pkg: VideoboothPackage): void {
        this.cartService.addItem({
            id: pkg.id,
            name: `Rolly Videobooth 360 - ${pkg.name}`,
            category: 'Videobooth',
            price: pkg.price,
            image: '/assets/visidea/360rolly-photobooth.PNG',
            selectedOption: pkg.name,
        });
    }

    checkout(pkg: VideoboothPackage): void {
        void this.cartService.startDirectCheckout({
            id: pkg.id,
            name: `Rolly Videobooth 360 - ${pkg.name}`,
            category: 'Videobooth',
            price: pkg.price,
            image: '/assets/visidea/360rolly-photobooth.PNG',
            selectedOption: pkg.name,
        });
    }

}
